/* ═══════════════════════════════════════════════════════════════
   PiARIS — PA11Y Lab Showcase
   Purple → Pink gradient. Lab beaker SVG, particles, triple-engine
   feature cards, and a code preview showing WCAG 1.1.1 detection
   with an AI-generated alt text fix.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { PRODUCT_GRADIENTS } from "./gradients";
import { ProductLockup } from "./ProductLockup";

var E = React.createElement;
var G = PRODUCT_GRADIENTS.a11ylab;

/** Lab beaker / flask SVG with bubbling liquid */
function BeakerSvg() {
  return E("svg", {
    width: 130, height: 140, viewBox: "0 0 130 140",
    "aria-hidden": "true",
    style: { filter: "drop-shadow(0 4px 16px rgba(255,255,255,0.25))" }
  },
    E("defs", null,
      E("linearGradient", { id: "al-liquid", x1: "0%", y1: "0%", x2: "0%", y2: "100%" },
        E("stop", { offset: "0%",  stopColor: "#F0ABFC", stopOpacity: 0.9 }),
        E("stop", { offset: "100%", stopColor: "#8B5CF6", stopOpacity: 0.95 })
      )
    ),
    /* Beaker neck */
    E("rect", {
      x: 52, y: 14, width: 26, height: 24,
      fill: "rgba(255,255,255,0.95)",
      stroke: "rgba(255,255,255,0.6)", strokeWidth: 1.5,
      rx: 2
    }),
    /* Beaker body — flask shape */
    E("path", {
      d: "M44 38 L86 38 L106 120 Q106 132 94 132 L36 132 Q24 132 24 120 Z",
      fill: "rgba(255,255,255,0.92)",
      stroke: "rgba(255,255,255,0.6)", strokeWidth: 1.5
    }),
    /* Liquid */
    E("path", {
      d: "M30 80 L100 80 L104 120 Q104 130 92 130 L38 130 Q26 130 26 120 Z",
      fill: "url(#al-liquid)"
    }),
    /* Bubbles */
    E("circle", { className: "al-bubble al-bubble-1", cx: 50, cy: 100, r: 4, fill: "rgba(255,255,255,0.7)" }),
    E("circle", { className: "al-bubble al-bubble-2", cx: 70, cy: 110, r: 3, fill: "rgba(255,255,255,0.7)" }),
    E("circle", { className: "al-bubble al-bubble-3", cx: 60, cy: 90, r: 2.5, fill: "rgba(255,255,255,0.7)" }),
    E("circle", { className: "al-bubble al-bubble-4", cx: 80, cy: 95, r: 3, fill: "rgba(255,255,255,0.7)" }),
    /* Measurement marks */
    [50, 65, 80, 95, 110].map(function(y, i) {
      return E("line", {
        key: i,
        x1: 28, y1: y, x2: 34, y2: y,
        stroke: "rgba(15,23,42,0.4)", strokeWidth: 1
      });
    })
  );
}

/** Floating particles */
function Particles() {
  var dots = [
    { l: 8,  t: 70, d: 0,    s: 5 },
    { l: 18, t: 88, d: 1.0,  s: 4 },
    { l: 30, t: 60, d: 0.5,  s: 6 },
    { l: 50, t: 82, d: 1.4,  s: 4 },
    { l: 65, t: 70, d: 0.7,  s: 5 },
    { l: 78, t: 90, d: 1.8,  s: 4 },
    { l: 88, t: 65, d: 0.3,  s: 6 }
  ];
  return E("div", {
    "aria-hidden": "true",
    style: {
      position: "absolute", inset: 0, overflow: "hidden",
      pointerEvents: "none"
    }
  },
    dots.map(function(d, i) {
      return E("div", {
        key: i,
        className: "al-particle",
        style: {
          position: "absolute",
          left: d.l + "%",
          top: d.t + "%",
          width: d.s,
          height: d.s,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.55)",
          animationDelay: d.d + "s"
        }
      });
    })
  );
}

/** Code preview: detected issue + AI fix */
function CodePreview() {
  return E("figure", {
    "aria-label": "Live WCAG 1.1.1 detection example",
    style: { margin: 0 }
  },
    E("figcaption", {
      style: {
        fontSize: 10,
        fontWeight: 700,
        color: "rgba(255,255,255,0.8)",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        marginBottom: 6
      }
    }, "WCAG 1.1.1 \u2014 Detected & Fixed"),
    /* Detected */
    E("div", {
      style: {
        background: "rgba(15,23,42,0.7)",
        border: "1px solid rgba(248,113,113,0.4)",
        borderRadius: 10,
        padding: "10px 12px",
        marginBottom: 6
      }
    },
      E("div", {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 5,
          fontSize: 9,
          fontWeight: 800,
          color: "#FCA5A5",
          textTransform: "uppercase",
          letterSpacing: ".04em"
        }
      },
        E("span", { "aria-hidden": "true" }, "\u2715"),
        " Missing alt text"
      ),
      E("pre", {
        style: {
          margin: 0,
          fontSize: 11,
          fontFamily: "'IBM Plex Mono', monospace",
          color: "#E2E8F0",
          overflowX: "auto"
        }
      },
        E("code", null,
          E("span", { style: { color: "#F472B6" } }, "<img "),
          E("span", { style: { color: "#FBBF24" } }, "src"),
          E("span", { style: { color: "#94A3B8" } }, "="),
          E("span", { style: { color: "#6EE7B7" } }, '"hero.jpg"'),
          E("span", { style: { color: "#F472B6" } }, " />")
        )
      )
    ),
    /* Fixed */
    E("div", {
      style: {
        background: "rgba(15,23,42,0.7)",
        border: "1px solid rgba(110,231,183,0.45)",
        borderRadius: 10,
        padding: "10px 12px"
      }
    },
      E("div", {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 5,
          fontSize: 9,
          fontWeight: 800,
          color: "#6EE7B7",
          textTransform: "uppercase",
          letterSpacing: ".04em"
        }
      },
        E("span", { "aria-hidden": "true" }, "\u2713"),
        " AI-generated fix"
      ),
      E("pre", {
        style: {
          margin: 0,
          fontSize: 11,
          fontFamily: "'IBM Plex Mono', monospace",
          color: "#E2E8F0",
          overflowX: "auto"
        }
      },
        E("code", null,
          E("span", { style: { color: "#F472B6" } }, "<img "),
          E("span", { style: { color: "#FBBF24" } }, "src"),
          E("span", { style: { color: "#94A3B8" } }, "="),
          E("span", { style: { color: "#6EE7B7" } }, '"hero.jpg"'),
          "\n     ",
          E("span", { style: { color: "#FBBF24" } }, "alt"),
          E("span", { style: { color: "#94A3B8" } }, "="),
          E("span", { style: { color: "#6EE7B7" } }, '"City skyline at sunset"'),
          E("span", { style: { color: "#F472B6" } }, " />")
        )
      )
    )
  );
}

function Pa11yLabShowcase(p) {
  var ctaLabel = p.ctaLabel || "Open PA11Y Lab";
  return E("section", {
    role: "region",
    "aria-labelledby": "al-showcase-title",
    style: {
      borderRadius: 18,
      overflow: "hidden",
      border: "2px solid " + G.border + "30",
      background: G.hero,
      color: "#FFFFFF",
      position: "relative",
      boxShadow: "0 10px 40px " + G.glow
    }
  },
    E(Particles, null),
    /* Header */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "28px 28px 20px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center"
      },
      className: "al-header"
    },
      E("div", null,
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10
          }
        },
          E(ProductLockup, { size: "small", white: true }),
          E("span", { "aria-hidden": "true", style: { color: "rgba(255,255,255,0.4)" } }, "/"),
          E("span", {
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              textTransform: "uppercase",
              letterSpacing: ".08em"
            }
          }, "PA11Y Lab")
        ),
        E("h2", {
          id: "al-showcase-title",
          style: {
            fontSize: 26,
            fontWeight: 900,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: "-0.4px"
          }
        }, "Multi-Engine Accessibility Bench"),
        E("p", {
          style: {
            fontSize: 13,
            color: "rgba(255,255,255,0.82)",
            margin: "8px 0 0",
            lineHeight: 1.55,
            maxWidth: 480
          }
        }, "Web Audit and Doc Audit with persistent sessions. Triple-engine analysis with axe-core, HTML_CodeSniffer, and AI. Remediation with alt text generation, fix scripts, and PDF guides.")
      ),
      E("div", { style: { flexShrink: 0 } }, E(BeakerSvg, null))
    ),
    /* Body */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "0 28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16
      },
      className: "al-body"
    },
      /* Triple feature cards */
      E("ul", {
        style: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
          listStyle: "none",
          padding: 0,
          margin: 0,
          gridColumn: "1 / 2"
        }
      },
        [
          { i: "\uD83E\uDD16", t: "AI-Powered",        d: "Vision models generate alt text, headings, fixes" },
          { i: "\u2699\uFE0F",   t: "Triple Engine",      d: "axe-core + HTML_CodeSniffer + AI ensemble" },
          { i: "\uD83D\uDCCD", t: "Visual Pinpointing", d: "Click an issue, see the exact element on the page" }
        ].map(function(f, i) {
          return E("li", {
            key: i,
            style: {
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 10,
              padding: "10px 12px"
            }
          },
            E("span", {
              "aria-hidden": "true",
              style: {
                fontSize: 18,
                lineHeight: 1,
                flexShrink: 0,
                marginTop: 2
              }
            }, f.i),
            E("div", null,
              E("div", { style: { fontSize: 12, fontWeight: 800, color: "#fff", marginBottom: 2 } }, f.t),
              E("div", { style: { fontSize: 10, color: "rgba(255,255,255,0.75)", lineHeight: 1.4 } }, f.d)
            )
          );
        })
      ),
      E("div", { style: { gridColumn: "2 / 3" } }, E(CodePreview, null)),
      /* CTA */
      E("div", {
        style: {
          gridColumn: "1 / -1",
          marginTop: 4,
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }
      },
        p.onCta ? E("button", {
          onClick: p.onCta,
          style: {
            minHeight: 44,
            padding: "10px 22px",
            borderRadius: 10,
            border: "none",
            background: "#FFFFFF",
            color: G.pri,
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
          }
        }, "\uD83E\uDDEA " + ctaLabel) : null
      )
    )
  );
}

export { Pa11yLabShowcase };
